import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarView,
  useExternalCalendarEvents,
  useCalendarSyncRealtimeEvents,
} from '@/features/calendar/public';
import type { CalendarViewType, CalendarEvent } from '@/features/calendar/public';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { taskToCalendarEvent } from '../../adapters';
import type { TaskFilters } from '../../types';
import { useOpenTask } from '../../hooks/useOpenTask';
import { useTaskCalendarTasks } from '../../hooks/useTaskCalendarTasks';

interface TaskCalendarViewProps {
  projectId: string;
  boardId: string;
  filters: TaskFilters;
}

function getVisibleRange(view: CalendarViewType, date: Date): { start: string; end: string } {
  const d = new Date(date);
  if (view === 'month') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }
  if (view === 'week' || view === 'work_week') {
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }
  if (view === 'agenda') {
    const start = new Date(d);
    const end = new Date(d);
    end.setDate(d.getDate() + 30);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }
  // day
  const iso = d.toISOString().slice(0, 10);
  return { start: iso, end: iso };
}

export function TaskCalendarView({ projectId, boardId, filters }: TaskCalendarViewProps) {
  const { t } = useTranslation('tasks');
  const [view, setView] = useState<CalendarViewType>('month');
  const [date, setDate] = useState(new Date());
  const openTask = useOpenTask();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const range = getVisibleRange(view, date);
  const { data, isLoading, isError, isTruncated } = useTaskCalendarTasks(
    projectId,
    boardId,
    filters,
    range,
  );

  // External events are user-personal context overlaid on the board — no
  // project/board filter exists in the backend (product decision 2026-07-29).
  // Best-effort overlay: its loading/error state never blocks the task view
  // (a failure surfaces via the global error toast instead).
  const externalEvents = useExternalCalendarEvents(range);
  useCalendarSyncRealtimeEvents(orgId ?? '');

  const events = [...(data ?? []).map(taskToCalendarEvent), ...externalEvents.data];

  function handleEventClick(event: CalendarEvent) {
    if (event.type === 'task') {
      openTask(event.id);
    }
  }

  return (
    <div className="flex h-full flex-col">
      {isTruncated && (
        <p
          className="text-muted-foreground px-4 py-1 text-xs"
          data-testid="tasks-calendar-truncated"
        >
          {t('calendarView.truncatedNotice')}
        </p>
      )}
      <CalendarView
        events={events}
        isLoading={isLoading}
        isError={isError}
        controlledView={view}
        controlledDate={date}
        onViewChange={setView}
        onDateChange={setDate}
        onEventClick={handleEventClick}
        hideToolbarExtras
      />
    </div>
  );
}
